import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  label: string;
  children: React.ReactElement;
  delay?: number;
  /** Horizontal alignment relative to the trigger. 'right' keeps the tooltip from overflowing right-edge containers. */
  align?: 'center' | 'right';
  /** Allow the label to wrap onto multiple lines (for longer messages). */
  wrap?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
  label,
  children,
  delay = 500,
  align = 'center',
  wrap = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom'>('bottom');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const showTooltip = (e: React.MouseEvent | React.FocusEvent) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    triggerRef.current = e.currentTarget as HTMLElement;

    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setPosition(spaceBelow < 50 && spaceAbove > spaceBelow ? 'top' : 'bottom');
    }

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  };

  const {
    onMouseEnter: origMouseEnter,
    onMouseLeave: origMouseLeave,
    onFocus: origOnFocus,
    onBlur: origOnBlur,
  } = children.props;

  const childWithProps = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      showTooltip(e);
      origMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hideTooltip();
      origMouseLeave?.(e);
    },
    onFocus: (e: React.FocusEvent) => {
      showTooltip(e);
      origOnFocus?.(e);
    },
    onBlur: (e: React.FocusEvent) => {
      hideTooltip();
      origOnBlur?.(e);
    },
    'aria-label': label,
  });

  return (
    <div className="relative inline-flex">
      {childWithProps}
      <div
        role="tooltip"
        className={`absolute z-[60] px-2 py-1 text-xs text-white bg-postdrop-dark border border-white/10 rounded-control shadow-md pointer-events-none transition-[opacity,transform] duration-125 ease-out-strong ${
          wrap ? 'w-max max-w-[240px]' : 'whitespace-nowrap'
        } ${position === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'} ${
          align === 'right' ? 'right-0' : 'left-1/2 -translate-x-1/2'
        } ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.97]'}`}
      >
        {label}
        <span
          aria-hidden="true"
          className={`absolute h-0 w-0 border-x-[5px] border-x-transparent ${
            align === 'right' ? 'right-3' : 'left-1/2 -translate-x-1/2'
          } ${
            position === 'top'
              ? 'top-full border-t-[5px] border-t-postdrop-dark'
              : 'bottom-full border-b-[5px] border-b-postdrop-dark'
          }`}
        />
      </div>
    </div>
  );
};
